// Account.js - With sharing features
import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/formatters';
import { useParams, useNavigate } from 'react-router-dom';
import socket from '../services/socket';
import StockChart from './StockChart';
import BalanceHistoryChart from './BalanceHistoryChart';
import { getTransactionHistory, closePosition } from '../services/trading';
import generateAvatar from '../utils/generateAvatar';
import PRESET_AVATARS from '../utils/presetAvatars';
import { getUserProfile } from '../services/auth';
import FlickerText from './FlickerText'; // Import FlickerText
import { useNotification } from '../context/NotificationContext'; // Import useNotification
import { API_BASE_URL } from '../config';
import '../styles/Portfolio.css'; // Terminal theme

const STOCK_DATA_POLLING_INTERVAL = 5000; // Poll every 5 seconds to match stock update frequency

const Account = ({ userId: loggedInUserId, marketState: propMarketState }) => {
    const { addNotification } = useNotification(); // Use the hook
    // Get userId from URL params (for viewing other profiles)
    const { userId: urlUserId } = useParams();
    const navigate = useNavigate();

    // Use URL userId if available, otherwise use logged-in user's ID
    const profileUserId = urlUserId || loggedInUserId;
    // Ensure isOwnProfile is false if no one is logged in
    const isOwnProfile = !!loggedInUserId && profileUserId === loggedInUserId;

    // Reference to track component mount state
    const isMounted = useRef(true);
    const dataFetchedRef = useRef(null);
    const chartSectionRef = useRef(null); // Create a ref for the chart section

    // State management
    const [user, setUser] = useState({
        balance: 10000,
        initialBalance: 10000,
        positions: [],
        performancePercent: 0,
        totalValue: 10000,
        shortId: '',
        username: '',
        profilePicture: null
    });
    const [stocks, setStocks] = useState({}); // Store current stock prices by ID { stockId: {currentPrice, ticker, basePrice, history, change} }
    const [loading, setLoading] = useState(true);
    const [socketConnected, setSocketConnected] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [closingPosition, setClosingPosition] = useState(null);
    const [localMarketState, setLocalMarketState] = useState(null);

    // Use prop market state or local state
    const marketState = propMarketState || localMarketState;

    // Check if trading/closing positions is allowed
    const canTrade = marketState?.isOpen && marketState?.tradingEnabled;
    const [userTeams, setUserTeams] = useState(null);
    const [showPicture, setShowPicture] = useState(null);
    const [searchTicker, setSearchTicker] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);

    // Transaction history
    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(true);

    // Rank
    const [rank, setRank] = useState(null);
    const [totalPlayers, setTotalPlayers] = useState(null);

    // Calculate rank tier based on position
    const getRankTier = (rank, total) => {
        if (!rank || !total) return 'bronze';
        const percentile = (rank / total) * 100;
        if (rank <= 3) return 'diamond';        // Top 3
        if (percentile <= 10) return 'platinum'; // Top 10%
        if (percentile <= 25) return 'gold';     // Top 25%
        if (percentile <= 50) return 'silver';   // Top 50%
        return 'bronze';                          // Bottom 50%
    };

    const rankTier = getRankTier(rank, totalPlayers);

    // Sharing feature states
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareLink, setShareLink] = useState('');
    const [shareCredentials, setShareCredentials] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Expandable position state
    const [expandedPositionId, setExpandedPositionId] = useState(null);

    // Edit profile states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editUsername, setEditUsername] = useState(user.username || '');
    const [editProfilePicture, setEditProfilePicture] = useState(user.profilePicture || '');

    // Create share link
    useEffect(() => {
        if (isOwnProfile && loggedInUserId) {
            const baseUrl = window.location.origin;
            const link = `${baseUrl}/portfolio/${loggedInUserId}`;
            setShareLink(link);

            // Get user credentials for sharing
            const fetchUserCredentials = async () => {
                try {
                    if (profileData && profileData.username) {
                        setShareCredentials(`Username: ${profileData.username}`);
                    } else if (user.shortId) {
                        setShareCredentials(`Login ID: ${user.shortId}`);
                    }
                } catch (error) {
                    console.error('Failed to get shareable credentials:', error);
                }
            };

            fetchUserCredentials();
        }
    }, [isOwnProfile, loggedInUserId, user.shortId]);

    // Scroll to top when navigating to a profile
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [profileUserId]);

    useEffect(() => {
        const fetchProfileData = async () => {
            if (!profileUserId) return;

            try {
                const data = await getUserProfile(profileUserId);
                if (isMounted.current) {
                    setProfileData(data);
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
            }
        };

        fetchProfileData();
    }, [profileUserId]);

    useEffect(() => {
        const fetchTeams = async () => {
            if (!profileUserId) return;
            try {
                const res = await fetch(`${API_BASE_URL}/api/user/${profileUserId}/teams`);
                const data = await res.json();
                if (data.success && isMounted.current) {
                    setUserTeams(data.teams);
                }
            } catch (err) {
                console.error('Error fetching teams:', err);
            }
        };
        fetchTeams();
    }, [profileUserId]);

    useEffect(() => {
        const handleMarketData = (stocksDataArray) => { // Assuming stocksDataArray is an array from market-data event
            setStocks(prevStocksMap => {
                const newStocksMap = { ...prevStocksMap };
                stocksDataArray.forEach(stock => {
                    newStocksMap[stock.id] = {
                        ...(prevStocksMap[stock.id] || {}),
                        currentPrice: parseFloat(stock.currentPrice),
                        ticker: stock.ticker,
                        basePrice: parseFloat(stock.basePrice),
                        history: stock.history || [],
                        // Calculate change if not provided directly by this event
                        change: stock.basePrice > 0 ? ((parseFloat(stock.currentPrice) / parseFloat(stock.basePrice) - 1) * 100) : 0
                    };
                });
                return newStocksMap;
            });
        };

        const handlePositionUpdate = (data) => {
            if (data.positions) {
                // Position updates often come with their own current prices.
                // We'll update user's positions and balance directly.
                // The effect watching `stocks` and `user.positions` will re-calculate performance.
                setUser(prevUser => {
                    // It's better if `data.positions` already contains calculated `currentValue` and `profit`
                    // based on the prices at the moment of the position update.
                    // If not, we might need to use the current `stocks` state here.
                    // For now, assuming `data.positions` are comprehensive.
                    const updatedPositions = data.positions; // Or process them if needed

                    return {
                        ...prevUser,
                        positions: updatedPositions,
                        balance: data.balance !== undefined ? parseFloat(data.balance) : prevUser.balance,
                    };
                });
            } else if (data.balance !== undefined) {
                 setUser(prevUser => ({
                    ...prevUser,
                    balance: parseFloat(data.balance)
                 }));
            }
        };

        const handleBalanceUpdate = (data) => {
            if (data.balance !== undefined) {
                setUser(prevUser => ({
                    ...prevUser,
                    balance: parseFloat(data.balance)
                }));
            }
        };

        const handleStockUpdates = (incomingUpdatesArray) => {
            console.log('Account.js received LIVE stock-updates from socket:', incomingUpdatesArray.map(u => ({ t: u.ticker, p: u.currentPrice })));
            setStocks(prevStocksMap => {
                const newStocksMap = { ...prevStocksMap };
                incomingUpdatesArray.forEach(update => {
                    const stockId = update.id;
                    const existingStockData = prevStocksMap[stockId] || {};
                    newStocksMap[stockId] = {
                        ...existingStockData,
                        currentPrice: parseFloat(update.currentPrice),
                        ticker: update.ticker,
                        change: parseFloat(update.change), // 'change' is a percentage
                        // Preserve basePrice and history if not in the update, or update if they are
                        basePrice: update.basePrice !== undefined ? parseFloat(update.basePrice) : existingStockData.basePrice,
                        history: update.history !== undefined ? update.history : existingStockData.history,
                        // You can also store tradingPressure and volume if needed for display
                        // tradingPressure: update.tradingPressure,
                        // volume: update.volume,
                    };
                });
                return newStocksMap;
            });
        };

        // Attach listeners
        socket.on('market-data', handleMarketData);
        socket.on('position-update', handlePositionUpdate);
        socket.on('balance-update', handleBalanceUpdate);
        socket.on('stock-updates', handleStockUpdates);

        // Market state listeners
        const handleMarketBell = (data) => {
            setLocalMarketState(prev => ({
                ...prev,
                isOpen: data.type === 'OPENING_BELL',
                tradingEnabled: data.type === 'OPENING_BELL'
            }));
        };
        const handleTradingPaused = () => {
            setLocalMarketState(prev => ({ ...prev, tradingEnabled: false }));
        };
        const handleTradingResumed = () => {
            setLocalMarketState(prev => ({ ...prev, tradingEnabled: true }));
        };

        const handlePartyStateUpdate = (data) => {
            if (data.marketState) {
                setLocalMarketState(prev => ({
                    ...prev,
                    isOpen: data.marketState.isOpen,
                    tradingEnabled: data.marketState.tradingEnabled
                }));
            }
        };

        socket.on('market-bell', handleMarketBell);
        socket.on('trading-paused', handleTradingPaused);
        socket.on('trading-resumed', handleTradingResumed);
        socket.on('party-state-update', handlePartyStateUpdate);

        // Cleanup on unmount
        return () => {
            socket.off('market-data', handleMarketData);
            socket.off('position-update', handlePositionUpdate);
            socket.off('balance-update', handleBalanceUpdate);
            socket.off('stock-updates', handleStockUpdates);
            socket.off('market-bell', handleMarketBell);
            socket.off('trading-paused', handleTradingPaused);
            socket.off('trading-resumed', handleTradingResumed);
            socket.off('party-state-update', handlePartyStateUpdate);
        };
    }, [profileUserId, loggedInUserId, isOwnProfile]); // Dependencies for re-subscribing socket listeners

    // Fetch market state on mount + poll every 15s
    useEffect(() => {
        const fetchMarketState = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/market/status`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.marketState) {
                        setLocalMarketState(data.marketState);
                    }
                }
            } catch (err) {
                console.error('Error fetching market state:', err);
            }
        };
        fetchMarketState();
        const pollInterval = setInterval(fetchMarketState, 15000);
        return () => clearInterval(pollInterval);
    }, []);

    // New useEffect for Polling stock data for Account page
    useEffect(() => {
        const pollAccountStockData = async () => {
            if (!isMounted.current) return; // Use the existing isMounted ref
            try {
                // console.log('[Account.js Polling] Fetching all stocks via poll...');
                const response = await fetch(`${API_BASE_URL}/api/stocks`);
                if (!response.ok) {
                    console.error('[Account.js Polling] HTTP error while polling stocks:', response.status);
                    return;
                }
                const polledStocksDataArray = await response.json();

                if (!isMounted.current) return;

                // Update the stocks map, similar to handleMarketData or handleStockUpdates
                setStocks(prevStocksMap => {
                    const newStocksMap = { ...prevStocksMap };
                    (polledStocksDataArray || []).forEach(stock => {
                        const currentPrice = parseFloat(stock.currentPrice) || parseFloat(stock.basePrice);
                        const basePrice = parseFloat(stock.basePrice) || currentPrice;
                        const change = basePrice > 0 ? ((currentPrice / basePrice - 1) * 100) : 0;

                        newStocksMap[stock.id] = {
                            ...(prevStocksMap[stock.id] || {}), // Preserve existing history etc. if not in poll
                            currentPrice: currentPrice,
                            ticker: stock.ticker,
                            basePrice: basePrice,
                            history: stock.history || (prevStocksMap[stock.id]?.history || []),
                            change: parseFloat(change.toFixed(2)),
                        };
                    });
                    // console.log('[Account.js Polling] Stocks map updated via poll.');
                    return newStocksMap;
                });

            } catch (error) {
                console.error('[Account.js Polling] Error polling stock data:', error);
            }
        };
        
        // Start polling
        const intervalId = setInterval(pollAccountStockData, STOCK_DATA_POLLING_INTERVAL);

        // Cleanup interval on component unmount
        return () => {
            clearInterval(intervalId);
        };
    }, []); // Empty dependency array, so it runs once on mount and cleans up on unmount

    // Effect for recalculating user portfolio details when relevant states change
    useEffect(() => {
        // Only recalculate if we have stock data
        if (Object.keys(stocks).length > 0) {
            const newUpdatedPositions = user.positions.length > 0
                ? updatePositionsWithCurrentPrices(user.positions, stocks)
                : [];

            const newTotalValue = calculateTotalValue(newUpdatedPositions, user.balance);
            const newPerformancePercent = calculatePerformance(
                user.initialBalance,
                user.balance,
                newUpdatedPositions
            );

            // Update user state if there are meaningful changes
            // This functional update ensures we're working with the latest state for comparison.
            setUser(prevUser => {
                // More precise check if positions array content has changed.
                // This is crucial to ensure live price updates in positions trigger re-render.
                const positionsContentChanged = 
                    prevUser.positions.length !== newUpdatedPositions.length ||
                    prevUser.positions.some((prevPos, index) => 
                        prevPos.currentValue !== newUpdatedPositions[index]?.currentValue ||
                        prevPos.profit !== newUpdatedPositions[index]?.profit ||
                        prevPos.currentPrice !== newUpdatedPositions[index]?.currentPrice
                    );

                if (positionsContentChanged ||
                    prevUser.totalValue !== newTotalValue ||
                    prevUser.performancePercent !== newPerformancePercent ||
                    prevUser.balance !== user.balance || // Check against the potentially updated user.balance
                    prevUser.initialBalance !== user.initialBalance // Check against user.initialBalance
                ) {
                    return {
                        ...prevUser,
                        balance: user.balance, 
                        initialBalance: user.initialBalance,
                        positions: newUpdatedPositions,
                        totalValue: newTotalValue,
                        performancePercent: newPerformancePercent
                    };
                }
                return prevUser; // No change needed
            });
        }
    }, [stocks, user.positions, user.balance, user.initialBalance]); // Dependencies

    // Fetch initial data via API
    useEffect(() => {
        // Reset mount state
        isMounted.current = true;

        // No user to fetch data for
        if (!profileUserId) {
            setLoading(false);
            return;
        }

        // Avoid duplicate fetches only if we already fetched THIS user's data
        if (dataFetchedRef.current === profileUserId) {
            console.log('Data already fetched for this user, skipping fetch');
            return;
        }

        const fetchInitialData = async () => {
            try {
                if (dataFetchedRef.current !== profileUserId) setLoading(true);
                setError(null);
                console.log('Fetching account data for userId:', profileUserId);

                // Fetch stocks data
                let stocksData = [];
                try {
                    const stocksResponse = await fetch(`${API_BASE_URL}/api/stocks`);
                    if (!stocksResponse.ok) throw new Error(`HTTP error ${stocksResponse.status}`);
                    stocksData = await stocksResponse.json();
                    console.log('Received stocks data:', stocksData.length, 'stocks');
                } catch (e) {
                    console.error('Failed to fetch stocks:', e);
                    throw new Error('Failed to fetch stock data');
                }

                // Create a map of stock prices
                const stocksMap = {};
                stocksData.forEach(stock => {
                    const currentPrice = parseFloat(stock.currentPrice) || parseFloat(stock.basePrice);
                    stocksMap[stock.id] = {
                        currentPrice: currentPrice,
                        ticker: stock.ticker,
                        basePrice: parseFloat(stock.basePrice),
                        history: stock.history || []
                    };
                });

                // Only update state if still mounted
                if (!isMounted.current) return;
                setStocks(stocksMap);

                // Fetch user account data + positions
                let userData = {
                    balance: 10000,
                    initialBalance: 10000,
                    shortId: 'Unknown'
                };
                let positions = [];

                try {
                    if (isOwnProfile) {
                        // Own profile: validate + separate positions fetch
                        const userResponse = await fetch(`${API_BASE_URL}/api/validate`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                userId: profileUserId,
                                token: localStorage.getItem('token')
                            })
                        });
                        if (!userResponse.ok) throw new Error(`HTTP error ${userResponse.status}`);
                        userData = await userResponse.json();

                        try {
                            const positionsResponse = await fetch(`${API_BASE_URL}/api/positions/${profileUserId}`);
                            if (positionsResponse.ok) positions = await positionsResponse.json();
                        } catch (e) {
                            console.error('Failed to fetch positions:', e);
                        }
                    } else {
                        // Other user: use /api/user/:id which returns everything including positions
                        const userResponse = await fetch(`${API_BASE_URL}/api/user/${profileUserId}`);
                        if (!userResponse.ok) throw new Error(`HTTP error ${userResponse.status}`);
                        const fullUserData = await userResponse.json();

                        userData = {
                            balance: fullUserData.balance,
                            initialBalance: fullUserData.initialBalance || fullUserData.balance,
                            shortId: fullUserData.shortId || 'Unknown',
                            username: fullUserData.username || '',
                            profilePicture: fullUserData.profilePicture || null
                        };
                        positions = fullUserData.positions || [];
                    }

                    console.log('Received user data:', userData, 'positions:', positions.length);
                } catch (e) {
                    console.error('Failed to fetch user data:', e);
                    throw new Error('Failed to fetch user account data');
                }

                if (isOwnProfile) {
                    try {
                        const profileData = await getUserProfile(profileUserId);
                        if (profileData) {
                            userData.username = profileData.username || '';
                            userData.profilePicture = profileData.profilePicture || null;
                        }
                    } catch (profileError) {
                        console.error('Failed to fetch profile data:', profileError);
                    }
                }

                // Calculate positions with current stock prices
                const updatedPositions = updatePositionsWithCurrentPrices(positions, stocksMap);

                // Determine a robust initialBalance for calculation and state
                const resolvedInitialBalance = 
                    (userData.initialBalance && userData.initialBalance > 0) 
                        ? userData.initialBalance 
                        : (userData.balance && userData.balance > 0 
                            ? userData.balance 
                            : 10000); // Default to 10000 if others are not positive

                // Calculate total value and performance using the resolvedInitialBalance
                const totalValue = calculateTotalValue(updatedPositions, userData.balance);
                const performancePercent = calculatePerformance(
                    resolvedInitialBalance, // Use the robust initial balance here
                    userData.balance,
                    updatedPositions
                );

                // Update user state
                setUser({
                    balance: userData.balance,
                    initialBalance: resolvedInitialBalance, // And store it in state
                    shortId: userData.shortId,
                    positions: updatedPositions,
                    totalValue,
                    performancePercent,
                    username: userData.username || '',
                    profilePicture: userData.profilePicture || null
                });

                // If user has positions, select the first stock by default for the chart
                if (updatedPositions.length > 0) {
                    setSelectedStock(updatedPositions[0].stockId);
                }

                // Mark which user's data was fetched
                dataFetchedRef.current = profileUserId;
                setLoading(false);
            } catch (error) {
                console.error('Error fetching account data:', error);
                if (isMounted.current) {
                    setError(`Failed to load account data: ${error.message}`);
                    setLoading(false);
                }
            }
        };

        fetchInitialData();

        // Cleanup function
        return () => {
            isMounted.current = false;
        };
    }, [profileUserId, loggedInUserId, isOwnProfile]);

    const fetchTransactions = async () => {
        try {
            setTransactionsLoading(true);

            // Only fetch transactions for own profile
            if (isOwnProfile) {
                const history = await getTransactionHistory(profileUserId);
                if (isMounted.current) {
                    setTransactions(history.transactions || []);
                }
            }
        } catch (error) {
            console.error('Error fetching transaction history:', error);
        } finally {
            if (isMounted.current) {
                setTransactionsLoading(false);
            }
        }
    };

    // Fetch transaction history
    useEffect(() => {
        if (!profileUserId) return;

        fetchTransactions();
    }, [profileUserId, isOwnProfile]);

    // Set up minimal socket listeners - ONLY for own profile
    useEffect(() => {
        // Only set up sockets for own profile
        if (!isOwnProfile) return;

        const handleConnect = () => {
            console.log('Socket connected');
            setSocketConnected(true);
        };

        const handleDisconnect = () => {
            console.log('Socket disconnected');
            setSocketConnected(false);
        };

        // Basic socket setup
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);

        // Set initial connection state
        setSocketConnected(socket.connected);

        // Clean up listeners
        return () => {
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
        };
    }, [isOwnProfile]);

    // Handle copy link to clipboard
    const copyLinkToClipboard = () => {
        navigator.clipboard.writeText(shareLink).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }).catch(err => {
            console.error('Failed to copy link:', err);
        });
    };

    // Handle ticker search
    const handleSearchTicker = async (searchValue) => {
        setSearchTicker(searchValue);

        if (searchValue.length < 1) {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/stocks`);
            if (response.ok) {
                const allStocks = await response.json();
                const filtered = allStocks
                    .filter(stock =>
                        stock.ticker.toLowerCase().includes(searchValue.toLowerCase()) ||
                        stock.name.toLowerCase().includes(searchValue.toLowerCase())
                    )
                    .slice(0, 8); // Limit to 8 results
                setSearchResults(filtered);
                setShowSearchResults(true);
            }
        } catch (error) {
            console.error('Error searching stocks:', error);
        }
    };

    const handleSearchSelect = (ticker) => {
        navigate(`/stock/${ticker}`);
        setSearchTicker('');
        setShowSearchResults(false);
    };

    const handleClosePosition = async (positionId) => {
        const position = user.positions.find(p => p.id === positionId);
        if (!position) {
            addNotification('Position not found to close.', 'error');
            return;
        }

        try {
            setClosingPosition(positionId);
            const result = await closePosition(profileUserId, positionId);

            const profitLoss = parseFloat(result.profitLoss) || 0;
            
            addNotification(
                `Closed ${position.ticker} position.`, 
                'success', 
                { profitLoss: profitLoss } // Pass profitLoss in details
            );
            
            if (result.balance !== undefined) {
                setUser(prevUser => ({
                    ...prevUser,
                    balance: parseFloat(result.balance),
                    positions: result.positions || prevUser.positions.filter(p => p.id !== positionId),
                }));
            } else if (result.positions) { // If only positions are returned
                setUser(prevUser => ({
                    ...prevUser,
                    positions: result.positions,
                }));
            } else { // Fallback if result is incomplete, just remove the position locally
                setUser(prevUser => ({
                    ...prevUser,
                    positions: prevUser.positions.filter(p => p.id !== positionId),
                }));
            }
            fetchTransactions();
        } catch (error) {
            console.error('Close position failed:', error);
            addNotification(`Failed to close ${position?.ticker || 'position'}: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setClosingPosition(null);
        }
    };

    // Handler for updates coming from StockChart component
    const handleChartPositionUpdate = (result, actionDetails) => {
        // actionDetails = { actionType: 'OPEN_LONG' | 'OPEN_SHORT' | 'CLOSE', stockTicker, tradeAmount?, entryPrice? }
        // result = { balance, positions, profitLoss?, position?, error? }

        if (result.error) {
            addNotification(`Chart action failed for ${actionDetails.stockTicker}: ${result.error}`, 'error');
            return;
        }

        let notificationMessage = '';
        let notificationDetails = null;

        if (actionDetails.actionType === 'CLOSE') {
            const profitLoss = parseFloat(result.profitLoss) || 0;
            notificationMessage = `Closed ${actionDetails.stockTicker} position via chart.`;
            notificationDetails = { profitLoss: profitLoss };
        } else if (actionDetails.actionType === 'OPEN_LONG' || actionDetails.actionType === 'OPEN_SHORT') {
            const actionVerb = actionDetails.actionType === 'OPEN_LONG' ? 'bought' : 'shorted';
            notificationMessage = `Successfully ${actionVerb} ${actionDetails.tradeAmount} ${actionDetails.stockTicker} @ ${formatCurrency(actionDetails.entryPrice)} via chart.`;
        }
        
        if (notificationMessage) {
            addNotification(notificationMessage, 'success', notificationDetails);
        }

        // Update Account's user state based on the result from StockChart
        if (result.balance !== undefined) {
            setUser(prevUser => ({
                ...prevUser,
                balance: result.balance,
                positions: result.positions || prevUser.positions 
            }));
        } else if (result.positions) { // If only positions are returned by the action
            setUser(prevUser => ({
                ...prevUser,
                positions: result.positions,
            }));
        }
        fetchTransactions(); // Refresh transactions if any trade action happened
    };

    // Handle stock selection for chart
    const handleStockSelect = (stockId) => {
        setSelectedStock(stockId);
        // After state update, the chart section might render or update
        // We use a timeout to allow the DOM to update before scrolling
        setTimeout(() => {
            if (chartSectionRef.current) {
                chartSectionRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'nearest' // 'start', 'center', 'end', or 'nearest'
                });
            }
        }, 0);
    };

    // Helper functions
    function updatePositionsWithCurrentPrices(positions, stockPricesMap) {
        if (!positions || !Array.isArray(positions)) return [];

        return positions.map(position => {
            const stockDataFromMap = stockPricesMap[position.stockId];
            
            // Ensure stockCurrentPrice is a number. Fallback to position's stored price or entry price.
            let stockCurrentPrice = position.currentPrice || position.entryPrice || 0;
            if (stockDataFromMap && stockDataFromMap.currentPrice !== undefined && stockDataFromMap.currentPrice !== null) {
                stockCurrentPrice = parseFloat(stockDataFromMap.currentPrice);
            } else if (stockDataFromMap && stockDataFromMap.basePrice !== undefined && stockDataFromMap.basePrice !== null) {
                stockCurrentPrice = parseFloat(stockDataFromMap.basePrice);
            }
            stockCurrentPrice = isNaN(stockCurrentPrice) ? (position.entryPrice || 0) : stockCurrentPrice;

            let currentValue;
            if (position.type === 'LONG') {
                currentValue = position.shares * stockCurrentPrice;
            } else { // SHORT
                const entryValue = position.shares * position.entryPrice;
                const priceDiff = position.entryPrice - stockCurrentPrice;
                currentValue = entryValue + (priceDiff * position.shares);
            }

            const profit = currentValue - position.initialValue;

            return {
                ...position,
                currentValue: parseFloat(currentValue.toFixed(2)),
                profit: parseFloat(profit.toFixed(2)),
                currentPrice: parseFloat(stockCurrentPrice.toFixed(2)) // Ensure this is updated
            };
        });
    }

    function calculateTotalValue(positions, cashBalance) {
        if (!positions || positions.length === 0) return cashBalance || 0;

        const positionsValue = positions.reduce((total, pos) => total + (pos.currentValue || 0), 0);
        return parseFloat(((cashBalance || 0) + positionsValue).toFixed(2));
    }

    function calculatePerformance(initialBalance, currentBalance, positions) {
        if (!initialBalance || initialBalance === 0) return 0;

        const totalValue = calculateTotalValue(positions, currentBalance);
        return parseFloat(((totalValue - initialBalance) / initialBalance * 100).toFixed(2));
    }

    function formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // Fetch user's rank from leaderboard
    useEffect(() => {
        if (!profileUserId) return;

        const fetchRank = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/leaderboard`);
                if (response.ok) {
                    const leaderboard = await response.json();
                    setTotalPlayers(leaderboard.length);
                    const userIndex = leaderboard.findIndex(entry => entry.id === profileUserId);
                    if (userIndex !== -1) {
                        setRank(userIndex + 1);
                    }
                }
            } catch (error) {
                console.error('Error fetching rank:', error);
            }
        };

        fetchRank();
        const rankInterval = setInterval(fetchRank, 30000); // Update every 30 seconds
        return () => clearInterval(rankInterval);
    }, [profileUserId]); // Only re-create interval when user changes

    // No user context - show login prompt
    if (!profileUserId) {
        return (
            <div className="account-container">
                <div className="account-no-user">
                    <h2>Welcome to Stock Market Party</h2>
                    <p>Log in or register to view your portfolio and start trading.</p>
                    <div className="account-no-user-actions">
                        <button onClick={() => navigate('/login')} className="login-btn">
                            Log In
                        </button>
                        <button onClick={() => navigate('/')} className="register-btn">
                            Register with QR Code
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="account-loading">
                <div className="spinner"></div>
                <p>Loading portfolio data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="account-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
            </div>
        );
    }

    // Get the selected stock data for the chart
    const selectedStockData = selectedStock ? stocks[selectedStock] : null;
    const displayUsername = profileData?.username || user.username || `Trader ${user.shortId}`;
    const displayPicture = profileData?.profilePicture || user.profilePicture || generateAvatar(displayUsername);

    return (
        <div className="account-container">
            {/* Market Status Banner - only show when can't trade */}
            {!canTrade && marketState && (
                <div className={`market-status-banner ${!marketState.isOpen ? 'closed' : 'paused'}`}>
                    <span className="banner-icon">{!marketState.isOpen ? '🔴' : '⏸️'}</span>
                    <span className="banner-text">
                        {!marketState.isOpen ? 'Market Closed' : 'Trading Paused'}
                    </span>
                    <span className="banner-subtitle">
                        {!marketState.isOpen
                            ? 'Positions cannot be closed until market opens'
                            : 'Positions cannot be closed during pause'}
                    </span>
                </div>
            )}

            {!isOwnProfile && (
                <div className="viewing-other-profile">
                    <button onClick={() => navigate('/portfolio')} className="back-to-own-profile">
                        &larr; Back to Your Portfolio
                    </button>
                    <h2>Viewing {displayUsername}'s Portfolio</h2>
                </div>
            )}

            {/* Stock Search - Robinhood Style - AT TOP */}
            {isOwnProfile && (
                <div className="stock-search-widget" style={{ background: '#fff', padding: '15px', marginBottom: '15px', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <input
                        type="text"
                        className="search-input-portfolio"
                        placeholder="🔍 Search stocks to trade..."
                        value={searchTicker}
                        onChange={(e) => handleSearchTicker(e.target.value)}
                        onFocus={() => searchTicker && setShowSearchResults(true)}
                        style={{ width: '100%', padding: '12px 15px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                    {showSearchResults && searchResults.length > 0 && (
                        <div className="search-results-dropdown" style={{ position: 'absolute', background: '#fff', border: '1px solid #ddd', marginTop: '5px', width: 'calc(100% - 30px)', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '6px' }}>
                            {searchResults.map(stock => (
                                <div
                                    key={stock.id}
                                    className="search-result-item"
                                    onClick={() => handleSearchSelect(stock.ticker)}
                                    style={{ padding: '12px 15px', borderBottom: '1px solid #eee', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                                >
                                    <div className="result-left">
                                        <div className="result-ticker" style={{ fontWeight: 'bold', fontSize: '1rem' }}>{stock.ticker}</div>
                                        <div className="result-name" style={{ fontSize: '0.75rem', color: '#666' }}>{stock.name}</div>
                                    </div>
                                    <div className="result-right">
                                        <div className="result-price" style={{ fontWeight: '600' }}>{formatCurrency(stock.currentPrice)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="account-header-compact">
                <div className="profile-top">
                    <img
                        src={displayPicture}
                        alt="Profile"
                        className="avatar-lg clickable-avatar"
                        onClick={() => setShowPicture(displayPicture)}
                        onError={(e) => { e.target.src = generateAvatar(user.username) }}
                    />
                    <div className="username-lg">{user.username || 'Trader'}</div>
                    {userTeams && Object.keys(userTeams).length > 0 && (
                        <div className="team-badges">
                            {['battleship', 'golf', 'briefcase'].filter(g => userTeams[g]).map(game => {
                                const c = userTeams[game].color;
                                return (
                                    <span
                                        key={game}
                                        className="team-badge"
                                        style={{
                                            color: c,
                                            borderColor: c,
                                            background: `${c}22`,
                                        }}
                                    >
                                        {userTeams[game].shortPrefix}:{userTeams[game].name} ({userTeams[game].team + 1})
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    {isOwnProfile && (
                        <div className="profile-top-actions">
                            <button className="profile-tiny-btn" onClick={() => setShowShareModal(true)} title="Share">
                                &#x2197;
                            </button>
                            <button className="profile-tiny-btn" onClick={() => {
                                setEditUsername(user.username || '');
                                setEditProfilePicture(user.profilePicture || '');
                                setShowEditModal(true);
                            }} title="Edit">
                                &#x270E;
                            </button>
                        </div>
                    )}
                </div>

                <div className="stats-row">
                    {rank && (
                        <div
                            className={`stat-compact rank-stat rank-${rankTier} clickable`}
                            onClick={() => navigate('/leaderboard')}
                            title="View Leaderboard"
                        >
                            <div className="stat-val rank-val">
                                <FlickerText value={`#${rank}`} />
                            </div>
                            <div className="stat-lbl">Rank{totalPlayers ? ` / ${totalPlayers}` : ''}</div>
                        </div>
                    )}
                    <div className="stat-compact">
                        <div className="stat-val">
                            <FlickerText value={formatCurrency(user.totalValue)} />
                        </div>
                        <div className="stat-lbl">Total Value</div>
                    </div>
                    <div className="stat-compact">
                        <div className={`stat-val ${user.performancePercent >= 0 ? 'gain' : 'loss'}`}>
                            <FlickerText value={`${user.performancePercent >= 0 ? '+' : ''}${user.performancePercent.toFixed(2)}%`} />
                        </div>
                        <div className="stat-lbl">Performance</div>
                    </div>
                    <div className="stat-compact">
                        <div className="stat-val">
                            <FlickerText value={formatCurrency(user.balance)} />
                        </div>
                        <div className="stat-lbl">Cash</div>
                    </div>
                    <div className="stat-compact">
                        <div className="stat-val">
                            <FlickerText value={formatCurrency(user.totalValue - user.balance)} />
                        </div>
                        <div className="stat-lbl">Positions</div>
                    </div>
                </div>
            </div>

            {isOwnProfile && (
                <div className="balance-history-section">
                    <BalanceHistoryChart userId={profileUserId} currentTotalValue={user.totalValue} initialBalance={user.initialBalance} />
                </div>
            )}


            {/* Position Summary - Expandable with Inline Chart */}
            <div className="positions-summary">
                <h3>Positions ({user.positions.length})</h3>
                {user.positions && user.positions.length > 0 ? (
                    <div className="positions-list">
                        {user.positions.map((position, index) => {
                            const profitPercent = position.initialValue
                                ? (position.profit / position.initialValue) * 100
                                : 0;
                            const isExpanded = expandedPositionId === position.id;
                            const positionStockData = stocks[position.stockId];

                            return (
                                <div key={`position-${position.id || index}`} className={`position-wrapper ${isExpanded ? 'expanded' : ''}`}>
                                    <div
                                        className="position-row"
                                        onClick={() => setExpandedPositionId(isExpanded ? null : position.id)}
                                    >
                                        <div className="position-left">
                                            <div className="position-ticker">
                                                {position.ticker}
                                                <span className={`position-type-badge ${position.type.toLowerCase()}`}>
                                                    {position.type}
                                                </span>
                                            </div>
                                            <div className="position-shares">{position.shares} share{position.shares > 1 ? 's' : ''}</div>
                                        </div>
                                        <div className="position-right">
                                            <div className="position-value">
                                                <FlickerText value={formatCurrency(position.currentValue)} />
                                            </div>
                                            <div className={`position-profit ${position.profit >= 0 ? 'gain' : 'loss'}`}>
                                                <FlickerText value={`${position.profit >= 0 ? '+' : ''}${formatCurrency(position.profit)} (${profitPercent >= 0 ? '+' : ''}${profitPercent.toFixed(2)}%)`} />
                                            </div>
                                        </div>
                                        <div className="expand-indicator">{isExpanded ? '▼' : '▶'}</div>
                                    </div>

                                    {/* Expanded Position View */}
                                    {isExpanded && (
                                        <div className="position-expanded">
                                            {/* Compact Chart */}
                                            {positionStockData && (
                                                <div className="position-chart-compact">
                                                    <StockChart
                                                        stockId={position.stockId}
                                                        ticker={position.ticker}
                                                        positions={[position]}
                                                        stockHistory={positionStockData.history}
                                                        isProMode={false}
                                                        userId={isOwnProfile ? profileUserId : null}
                                                        onPositionUpdate={handleChartPositionUpdate}
                                                        compact={true}
                                                    />
                                                </div>
                                            )}

                                            {/* Position Details */}
                                            <div className="position-details-grid">
                                                <div className="detail-item">
                                                    <span className="detail-label">Entry Price</span>
                                                    <span className="detail-value">{formatCurrency(position.entryPrice)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Current Price</span>
                                                    <span className="detail-value">{formatCurrency(position.currentPrice)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Initial Value</span>
                                                    <span className="detail-value">{formatCurrency(position.initialValue)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Current Value</span>
                                                    <span className="detail-value">{formatCurrency(position.currentValue)}</span>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="position-actions">
                                                {isOwnProfile && (
                                                    <button
                                                        className="close-position-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClosePosition(position.id);
                                                        }}
                                                        disabled={closingPosition === position.id || !canTrade}
                                                        title={!canTrade ? (marketState?.isOpen ? 'Trading paused' : 'Market closed') : ''}
                                                    >
                                                        {closingPosition === position.id ? 'Closing...' : 'Close Position'}
                                                    </button>
                                                )}
                                                <button
                                                    className="view-stock-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/stock/${position.ticker}`);
                                                    }}
                                                >
                                                    View {position.ticker} →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="no-positions">
                        <p>{isOwnProfile
                            ? "You don't have any open positions. Start trading in the Market tab!"
                            : "This trader doesn't have any open positions."}</p>
                    </div>
                )}
            </div>

            {/* Transaction History - Only for own account */}
            {isOwnProfile && (
                <div className="transaction-history-section">
                    <h3>Transaction History</h3>

                    {/* Trading Stats */}
                    {(() => {
                        const closedTrades = transactions.filter(t => t.type === 'TRADE_CLOSE' && t.position_profit_loss != null);
                        if (closedTrades.length === 0) return null;

                        const pnls = closedTrades.map(t => parseFloat(t.position_profit_loss) || 0);
                        const wins = pnls.filter(p => p > 0);
                        const losses = pnls.filter(p => p < 0);
                        const totalPnl = pnls.reduce((a, b) => a + b, 0);
                        const biggestWin = wins.length > 0 ? Math.max(...wins) : 0;
                        const biggestLoss = losses.length > 0 ? Math.min(...losses) : 0;
                        const winRate = ((wins.length / closedTrades.length) * 100).toFixed(0);
                        const avgWin = wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
                        const avgLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;

                        return (
                            <div className="trading-stats-summary">
                                <div className="trading-stats-grid">
                                    <div className="trading-stat">
                                        <span className="stat-label">Trades</span>
                                        <span className="stat-value">{closedTrades.length}</span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">Win Rate</span>
                                        <span className={`stat-value ${parseInt(winRate) >= 50 ? 'positive' : 'negative'}`}>{winRate}%</span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">W / L</span>
                                        <span className="stat-value">
                                            <span className="positive">{wins.length}</span>
                                            {' / '}
                                            <span className="negative">{losses.length}</span>
                                        </span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">Total P&L</span>
                                        <span className={`stat-value ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
                                            {totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}
                                        </span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">Best Trade</span>
                                        <span className="stat-value positive">{biggestWin > 0 ? `+${formatCurrency(biggestWin)}` : '—'}</span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">Worst Trade</span>
                                        <span className="stat-value negative">{biggestLoss < 0 ? formatCurrency(biggestLoss) : '—'}</span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">Avg Win</span>
                                        <span className="stat-value positive">{avgWin > 0 ? `+${formatCurrency(avgWin)}` : '—'}</span>
                                    </div>
                                    <div className="trading-stat">
                                        <span className="stat-label">Avg Loss</span>
                                        <span className="stat-value negative">{avgLoss < 0 ? formatCurrency(avgLoss) : '—'}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {transactionsLoading ? (
                        <div className="transaction-loading">
                            <div className="spinner-sm"></div>
                            <p>Loading transaction history...</p>
                        </div>
                    ) : transactions.length > 0 ? (
                        <div className="transaction-table-container">
                            {/* Desktop table */}
                            <table className="transaction-table desktop-only">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>P&L</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((transaction, index) => {
                                        const amount = parseFloat(transaction.amount) || 0;
                                        const balanceAfter = parseFloat(transaction.balance_after) || 0;
                                        const pnl = transaction.type === 'TRADE_CLOSE' ? (parseFloat(transaction.position_profit_loss) || null) : null;

                                        return (
                                            <tr key={transaction.id || index} className={`transaction-row ${transaction.type.toLowerCase()}`}>
                                                <td>{formatDate(transaction.timestamp)}</td>
                                                <td className="transaction-type">
                                                    {transaction.type === 'TRADE_OPEN' ? 'Open' :
                                                        transaction.type === 'TRADE_CLOSE' ? 'Close' :
                                                            transaction.type === 'BONUS' ? 'Bonus' :
                                                                transaction.type === 'ADMIN_BONUS' ? 'Bonus' :
                                                                    transaction.type === 'ADMIN_PENALTY' ? 'Penalty' : transaction.type}
                                                </td>
                                                <td className="transaction-description">{transaction.description}</td>
                                                <td className={`transaction-amount ${amount >= 0 ? 'positive' : 'negative'}`}>
                                                    {formatCurrency(amount)}
                                                </td>
                                                <td className={`transaction-pnl ${pnl !== null ? (pnl >= 0 ? 'positive' : 'negative') : ''}`}>
                                                    {pnl !== null ? `${pnl >= 0 ? '+' : ''}${formatCurrency(pnl)}` : '—'}
                                                </td>
                                                <td className="transaction-balance">{formatCurrency(balanceAfter)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Mobile cards */}
                            <div className="transaction-cards mobile-only">
                                {transactions.map((transaction, index) => {
                                    const amount = parseFloat(transaction.amount) || 0;
                                    const balanceAfter = parseFloat(transaction.balance_after) || 0;
                                    const pnl = transaction.type === 'TRADE_CLOSE' ? (parseFloat(transaction.position_profit_loss) || null) : null;
                                    const ticker = transaction.stock_ticker || null;
                                    const typeLabel = transaction.type === 'TRADE_OPEN' ? 'Open' :
                                        transaction.type === 'TRADE_CLOSE' ? 'Close' :
                                            transaction.type === 'BONUS' ? 'Bonus' :
                                                transaction.type === 'ADMIN_BONUS' ? 'Bonus' :
                                                    transaction.type === 'ADMIN_PENALTY' ? 'Penalty' : transaction.type;

                                    return (
                                        <div key={transaction.id || index} className={`transaction-card ${transaction.type.toLowerCase()}`}>
                                            <div className="txn-card-top">
                                                <span className="txn-card-type">{typeLabel}{ticker ? ` · ${ticker}` : ''}</span>
                                                <span className="txn-card-date">{formatDate(transaction.timestamp)}</span>
                                            </div>
                                            <div className="txn-card-desc">{transaction.description}</div>
                                            <div className="txn-card-bottom">
                                                <span className={`txn-card-amount ${amount >= 0 ? 'positive' : 'negative'}`}>
                                                    {formatCurrency(amount)}
                                                </span>
                                                {pnl !== null && (
                                                    <span className={`txn-card-pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
                                                        P&L: {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
                                                    </span>
                                                )}
                                                <span className="txn-card-balance">Bal: {formatCurrency(balanceAfter)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="no-transactions">
                            <p>No transaction history found.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Share Modal */}
            {showShareModal && (
                <div className="share-modal-overlay">
                    <div className="share-modal">
                        <div className="share-modal-header">
                            <h3>Share Your Portfolio</h3>
                            <button
                                className="close-modal-btn"
                                onClick={() => setShowShareModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="share-modal-content">
                            <p>Share your portfolio with others:</p>

                            <div className="share-link-container">
                                <input
                                    type="text"
                                    value={shareLink}
                                    readOnly
                                    className="share-link-input"
                                />
                                <button
                                    className="copy-link-btn"
                                    onClick={copyLinkToClipboard}
                                >
                                    {copySuccess ? 'Copied!' : 'Copy Link'}
                                </button>
                            </div>

                            {shareCredentials && (
                                <>
                                    {/* <div className="share-credentials">
                                    <h4>Login Credentials</h4>
                                    
                                    <div className="credentials-box">
                                        {shareCredentials}
                                    </div>
                                </div> */}
                                </>
                            )}

                            {/* <div className="share-options">
                <p>Or share directly:</p>
                <div className="share-buttons">
                  <button className="share-btn twitter" onClick={() => window.open(`https://twitter.com/intent/tweet?text=Check out my Stock Market Party portfolio!&url=${encodeURIComponent(shareLink)}`, '_blank')}>
                    Twitter
                  </button>
                  <button className="share-btn facebook" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`, '_blank')}>
                    Facebook
                  </button>
                  <button className="share-btn email" onClick={() => window.open(`mailto:?subject=Check out my Stock Market Party Portfolio&body=Here's my trading portfolio: ${encodeURIComponent(shareLink)}`, '_blank')}>
                    Email
                  </button>
                </div>
              </div> */}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Profile Modal */}
            {showEditModal && (
                <div className="edit-profile-modal-overlay">
                    <div className="edit-profile-modal">
                        <h3>Edit Profile</h3>
                        <label>
                            Username:
                            <input
                                type="text"
                                value={editUsername}
                                onChange={e => setEditUsername(e.target.value)}
                                placeholder="Enter username"
                            />
                        </label>

                        <div className="profile-picture-section">
                            <label>Profile Picture:</label>

                            {/* Current preview */}
                            {editProfilePicture && (
                                <div className="picture-preview">
                                    <img
                                        src={editProfilePicture}
                                        alt="Preview"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}

                            {/* Preset avatars */}
                            <div className="preset-avatars-section">
                                <span className="preset-label">Choose a preset:</span>
                                <div className="preset-avatars-grid">
                                    {PRESET_AVATARS.map((avatar, i) => (
                                        <img
                                            key={i}
                                            src={avatar}
                                            alt={`Preset ${i + 1}`}
                                            className={`preset-avatar ${editProfilePicture === avatar ? 'selected' : ''}`}
                                            onClick={() => setEditProfilePicture(avatar)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* File upload */}
                            <div className="upload-option">
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                                    id="avatar-upload"
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;

                                        // Check file size (5MB limit)
                                        if (file.size > 5 * 1024 * 1024) {
                                            alert('File too large. Max 5MB.');
                                            return;
                                        }

                                        const formData = new FormData();
                                        formData.append('avatar', file);
                                        formData.append('userId', profileUserId);

                                        try {
                                            const res = await fetch(`${API_BASE_URL}/api/profile/upload`, {
                                                method: 'POST',
                                                body: formData
                                            });
                                            const data = await res.json();
                                            if (data.success) {
                                                setEditProfilePicture(data.imageUrl);
                                            } else {
                                                alert(data.message || 'Upload failed');
                                            }
                                        } catch (err) {
                                            alert('Error uploading image');
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    className="upload-btn"
                                    onClick={() => document.getElementById('avatar-upload').click()}
                                >
                                    Upload Photo
                                </button>
                            </div>

                            {/* Or enter URL */}
                            <div className="url-option">
                                <span className="divider-text">or enter URL</span>
                                <input
                                    type="text"
                                    value={editProfilePicture}
                                    onChange={e => setEditProfilePicture(e.target.value)}
                                    placeholder="https://..."
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button onClick={async () => {
                                try {
                                    const res = await fetch(`${API_BASE_URL}/api/profile/update`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            userId: profileUserId,
                                            username: editUsername,
                                            profilePicture: editProfilePicture
                                        })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                        setShowEditModal(false);
                                        window.location.reload();
                                    } else {
                                        alert(data.message || 'Failed to update profile');
                                    }
                                } catch (err) {
                                    alert('Error updating profile');
                                }
                            }}>Save</button>
                            <button onClick={() => setShowEditModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Picture Lightbox */}
            {showPicture && (
                <div className="picture-lightbox" onClick={() => setShowPicture(null)}>
                    <img src={showPicture} alt="Profile" onError={(e) => { e.target.src = generateAvatar(user.username); }} />
                </div>
            )}
        </div>
    );
};

export default Account;